import bowser from "bowser";
import {
  getSelectorsByUserAgent,
  mobileModel,
  mobileVendor,
  osName,
} from "react-device-detect";
import type {
  BatteryInfo,
  BrowserInfo,
  DeviceInfo,
  OSInfo,
  ScreenInfo,
} from "../types/eonStats";

interface NativeDeviceBridge {
  getDeviceMake?: () => string;
  getDeviceModel?: () => string;
  getDeviceResolution?: () => number;
  getDeviceFirmware?: () => string;
  getDeviceOsVersion?: () => string;
  getDeviceId?: () => string;
  getDeviceRefreshRate?: () => number;
  getDeviceWifiInfo?: () => string; // JSON, see NativeWifiInfo
  getDeviceThermalState?: () => string;
  getDeviceRamPressure?: () => string;
}

declare global {
  interface Window {
    DeviceBridge?: NativeDeviceBridge;
  }
}

interface NativeWifiInfo {
  band?: string;
  signal?: number;
  rssi?: number;
  linkSpeedMbps?: number;
  txLinkSpeedMbps?: number;
  rxLinkSpeedMbps?: number;
}

function getWifiInfo(): NativeWifiInfo {
  const raw = window.DeviceBridge?.getDeviceWifiInfo?.();
  if (!raw) return {};
  try {
    return JSON.parse(raw) as NativeWifiInfo;
  } catch {
    return {};
  }
}

interface TizenProductInfo {
  getRealModel?: () => string;
  getFirmware?: () => string;
  getDuid?: () => string;
  is8KPanelSupported?: () => boolean;
  isUdPanelSupported?: () => boolean;
}

interface WebosDeviceInfo {
  modelName?: string;
  screenHeight?: number;
  firmwareVersion?: string;
  version?: string;
  sdkVersion?: string; // webOS.deviceInfo
  platformVersion?: string; // PalmSystem.deviceInfo
}

interface BatteryManagerLike {
  level: number;
  charging: boolean;
}

export interface DeviceInfoPayload<T = string> {
  make: string;
  model: string;
  os: string;
  resolution: number;
  type: T;
}

// ----- Platform detection TV (Samsung, android, LG) -----
function getTizenProductInfo(): TizenProductInfo | undefined {
  const w = window as unknown as {
    webapis?: { productinfo?: TizenProductInfo };
  };
  return w.webapis?.productinfo;
}
function getWebosDeviceInfoFn():
  | ((cb: (info: WebosDeviceInfo) => void) => void)
  | undefined {
  const w = window as unknown as {
    webOS?: { deviceInfo?: (cb: (info: WebosDeviceInfo) => void) => void };
  };
  return w.webOS?.deviceInfo;
}

function getPalmSystemInfo(): WebosDeviceInfo | undefined {
  const palm = (window as unknown as { PalmSystem?: { deviceInfo?: string } })
    .PalmSystem;
  if (!palm?.deviceInfo) return undefined;
  try {
    return JSON.parse(palm.deviceInfo) as WebosDeviceInfo;
  } catch {
    return undefined;
  }
}

function getPalmSystemModel(): string | undefined {
  return getPalmSystemInfo()?.modelName;
}

function getWebosVersion(): string {
  return (
    webosInfoCache?.sdkVersion || getPalmSystemInfo()?.platformVersion || ""
  );
}

function getTizenVersion(): string {
  let capability: unknown;
  try {
    capability = (
      window as unknown as {
        tizen?: { systeminfo?: { getCapability?: (key: string) => unknown } };
      }
    ).tizen?.systeminfo?.getCapability?.(
      "http://tizen.org/feature/platform.version",
    );
  } catch {
    capability = undefined;
  }
  if (typeof capability === "string" && capability) return capability;
  return /Tizen (\d+\.\d+)/.exec(navigator.userAgent)?.[1] ?? "";
}

const isTizen = (): boolean => getTizenProductInfo() !== undefined;
const isWebos = (): boolean => getWebosDeviceInfoFn() !== undefined;
// -------------------------

function pruneUndefined(info: DeviceInfo): DeviceInfo {
  (Object.keys(info) as (keyof DeviceInfo)[]).forEach((key) => {
    if (info[key] === undefined) delete info[key];
  });
  return info;
}

// ---- all function to get device information ----
const UNAVAILABLE = "";

// webOS deviceInfo is async (callback); prime + cache at module load
let webosInfoCache: WebosDeviceInfo | null = null;
try {
  getWebosDeviceInfoFn()?.((info) => {
    webosInfoCache = info;
  });
} catch {
  webosInfoCache = null;
}

// Refresh rate fallback for platforms without a native API (Tizen/webOS/web):
// estimate via rAF and cache. Sampled after load + a settle delay, because
// sampling while the app is still booting measures jank, not the display.
let refreshRateCache: number | undefined;
function primeRefreshRate(): void {
  if (typeof requestAnimationFrame !== "function") return;
  const samples: number[] = [];
  let last = 0;
  let frames = 0;
  const tick = (t: number) => {
    if (last) samples.push(1000 / (t - last));
    last = t;
    if (++frames < 30) {
      requestAnimationFrame(tick);
    } else if (samples.length) {
      const sorted = [...samples].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      refreshRateCache = Math.round(
        sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid],
      );
    }
  };
  requestAnimationFrame(tick);
}
function scheduleRefreshRatePrime(): void {
  const start = () => window.setTimeout(primeRefreshRate, 1000);
  if (document.readyState === "complete") start();
  else window.addEventListener("load", start, { once: true });
}
scheduleRefreshRatePrime();

// Audio input presence: async enumerate; prime + cache at module load
let audioInputCache: boolean | undefined;
function primeAudioInput(): void {
  try {
    navigator.mediaDevices
      ?.enumerateDevices?.()
      .then((devices) => {
        audioInputCache = devices.some((d) => d.kind === "audioinput");
      })
      .catch(() => {
        audioInputCache = undefined;
      });
  } catch {
    audioInputCache = undefined;
  }
}
primeAudioInput();

interface UaDataHighEntropy {
  model?: string;
  platformVersion?: string;
}
let uaDataCache: UaDataHighEntropy | undefined;
function primeUserAgentData(): Promise<void> {
  try {
    const uaData = (
      navigator as Navigator & {
        userAgentData?: {
          getHighEntropyValues?: (
            hints: string[],
          ) => Promise<UaDataHighEntropy>;
        };
      }
    ).userAgentData;
    const pending = uaData?.getHighEntropyValues?.([
      "model",
      "platformVersion",
    ]);
    if (!pending) return Promise.resolve();
    return pending
      .then((values) => {
        uaDataCache = values;
      })
      .catch(() => {
        uaDataCache = undefined;
      });
  } catch {
    uaDataCache = undefined;
    return Promise.resolve();
  }
}

export const deviceInfoReady: Promise<void> = primeUserAgentData();

// Tizen Wi-Fi signal strength: async getPropertyValue; prime + cache at load
let tizenSignalCache: string | undefined;
function primeTizenSignal(): void {
  if (!isTizen()) return;
  const systeminfo = (
    window as unknown as {
      tizen?: {
        systeminfo?: {
          getPropertyValue?: (
            property: string,
            onSuccess: (data: { signalStrength?: number }) => void,
            onError?: (err: unknown) => void,
          ) => void;
        };
      };
    }
  ).tizen?.systeminfo;
  try {
    systeminfo?.getPropertyValue?.(
      "WIFI_NETWORK",
      (data) => {
        if (typeof data.signalStrength === "number") {
          const raw = data.signalStrength;
          tizenSignalCache = String(Math.round(raw <= 1 ? raw * 100 : raw));
        }
      },
      () => {
        tizenSignalCache = undefined;
      },
    );
  } catch {
    tizenSignalCache = undefined;
  }
}
primeTizenSignal();

// CPU/thermal pressure (Compute Pressure API — Chromium only): async; prime + cache
let thermalStateCache: string | undefined;
function primeThermalState(): void {
  const PressureObserverCtor = (
    window as unknown as {
      PressureObserver?: new (
        callback: (records: { state?: string }[]) => void,
      ) => { observe: (source: string) => Promise<void> };
    }
  ).PressureObserver;
  if (typeof PressureObserverCtor !== "function") return;
  try {
    const observer = new PressureObserverCtor((records) => {
      const latest = records[records.length - 1];
      if (latest?.state) thermalStateCache = latest.state;
    });
    observer.observe("cpu").catch(() => {
      thermalStateCache = undefined;
    });
  } catch {
    thermalStateCache = undefined;
  }
}
primeThermalState();

// Individual detectors (sync unless noted)
export function getBrowserInfo(): BrowserInfo {
  const parser = bowser.getParser(window.navigator.userAgent);
  return {
    useragent: navigator.userAgent,
    name: parser.getBrowserName(),
    version: parser.getBrowserVersion(),
  };
}

// make and model
export function getMakeAndModel(): { make: string; model: string } {
  const bridgeMake = window.DeviceBridge?.getDeviceMake?.() || UNAVAILABLE;
  const bridgeModel = window.DeviceBridge?.getDeviceModel?.() || UNAVAILABLE;
  if (bridgeMake || bridgeModel) {
    return { make: bridgeMake, model: bridgeModel };
  }

  if (isTizen()) {
    try {
      return {
        make: "Samsung",
        model: getTizenProductInfo()?.getRealModel?.() || UNAVAILABLE,
      };
    } catch {
      return { make: "Samsung", model: UNAVAILABLE };
    }
  }
  if (isWebos()) {
    return {
      make: "LG",
      model: webosInfoCache?.modelName || getPalmSystemModel() || UNAVAILABLE,
    };
  }

  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod|Macintosh/i.test(ua)) {
    const isIpad =
      /iPad/i.test(ua) ||
      (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    const model = /iPod/i.test(ua)
      ? "iPod"
      : /iPhone/i.test(ua)
      ? "iPhone"
      : isIpad
      ? "iPad"
      : "Mac";
    return { make: "Apple", model };
  }

  if (mobileVendor && mobileVendor !== "none") {
    return {
      make: mobileVendor,
      model: mobileModel && mobileModel !== "none" ? mobileModel : UNAVAILABLE,
    };
  }

  const uaDataModel = uaDataCache?.model;
  if (uaDataModel) {
    const { mobileVendor: vendor } = getSelectorsByUserAgent(
      `Mozilla/5.0 (Linux; Android 13; ${uaDataModel}) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36`,
    );
    return {
      make: vendor && vendor !== "none" ? vendor : UNAVAILABLE,
      model: uaDataModel,
    };
  }

  return { make: UNAVAILABLE, model: UNAVAILABLE };
}

// Resolution
function getTizenResolution(): number | null {
  try {
    const productinfo = getTizenProductInfo();
    if (productinfo?.is8KPanelSupported?.()) return 4320;
    if (productinfo?.isUdPanelSupported?.()) return 2160;
    return 1080;
  } catch {
    return null;
  }
}
export function getResolution(): number {
  if (window.DeviceBridge?.getDeviceResolution) {
    const res = window.DeviceBridge.getDeviceResolution();
    if (res) return res;
  }
  if (isWebos() && webosInfoCache?.screenHeight) {
    return webosInfoCache.screenHeight;
  }
  if (isTizen()) {
    const tizenHeight = getTizenResolution();
    if (tizenHeight) return tizenHeight;
  }
  return Math.round(window.screen.height * (window.devicePixelRatio || 1));
}

export function getScreenInfo(): ScreenInfo {
  return {
    width: window.screen.width,
    height: window.screen.height,
    dpr: window.devicePixelRatio || 1,
  };
}

export function getFirmware(): string | undefined {
  if (window.DeviceBridge?.getDeviceFirmware) {
    return window.DeviceBridge.getDeviceFirmware() || undefined;
  }
  if (isTizen()) {
    try {
      return getTizenProductInfo()?.getFirmware?.() || undefined;
    } catch {
      return undefined;
    }
  }
  if (isWebos()) {
    return webosInfoCache?.firmwareVersion ?? webosInfoCache?.version;
  }
  return undefined;
}

export function getDeviceId(): string | undefined {
  if (window.DeviceBridge?.getDeviceId) {
    return window.DeviceBridge.getDeviceId() || undefined;
  }
  if (isTizen()) {
    try {
      return getTizenProductInfo()?.getDuid?.() || undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export function getCpuCores(): number | undefined {
  return navigator.hardwareConcurrency || undefined;
}

export function getRamGB(): number | undefined {
  const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
  return typeof mem === "number" ? mem : undefined;
}

export async function getBatteryInfo(): Promise<BatteryInfo | undefined> {
  const getBattery = (
    navigator as unknown as {
      getBattery?: () => Promise<BatteryManagerLike>;
    }
  ).getBattery;
  if (typeof getBattery !== "function") return undefined;
  try {
    const battery = await getBattery.call(navigator);
    return { level: battery.level, charging: battery.charging };
  } catch {
    return undefined;
  }
}

export function getTimezone(): string {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    tz = undefined;
  }
  if (tz && /[A-Za-z]/.test(tz)) return tz; // valid IANA name or "UTC"

  const offset = -new Date().getTimezoneOffset(); // minutes east of UTC
  const sign = offset >= 0 ? "+" : "-";
  const abs = Math.abs(offset);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
}

function getOsInfo(): OSInfo {
  if (isTizen()) return { name: "Tizen", version: getTizenVersion() };
  if (isWebos()) return { name: "webOS", version: getWebosVersion() };
  const ua = window.navigator.userAgent;
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) {
    return { name: "iPadOS", version: /Version\/(\d+)/.exec(ua)?.[1] ?? "" };
  }
  const parser = bowser.getParser(ua);
  const rddOsName = osName && osName !== "none" ? osName : "";
  const rawName = parser.getOSName() || rddOsName || "";
  const name = rawName === "WebOS" ? "webOS" : rawName;

  if (name === "webOS") return { name, version: getWebosVersion() };
  if (name === "Tizen") return { name, version: getTizenVersion() };
  return {
    name,
    version:
      window.DeviceBridge?.getDeviceOsVersion?.() ||
      (name === "Android" && uaDataCache?.platformVersion) ||
      parser.getOSVersion() ||
      "",
  };
}
function collectCoreInfo(): {
  make: string;
  model: string;
  os: OSInfo;
  resolution: number;
} {
  const { make, model } = getMakeAndModel();
  return { make, model, os: getOsInfo(), resolution: getResolution() };
}
export function buildDeviceInfo<T>(type: T): DeviceInfoPayload<T> {
  const { make, model, os, resolution } = collectCoreInfo();
  return {
    make,
    model,
    os: os.name,
    resolution,
    type,
  };
}

function derivePlatform(type?: string): string {
  if (type === "TV") return "tv";
  if (type?.startsWith("MOBILE")) return "mobile";
  if (type?.startsWith("TABLET")) return "tablet";
  if (type?.startsWith("DESKTOP")) return "desktop";
  return bowser.getParser(window.navigator.userAgent).getPlatformType();
}

export async function collectDeviceInfo(type?: string): Promise<DeviceInfo> {
  const platform = derivePlatform(type);
  const wifi = getWifiInfo(); // one bridge call feeds every Wi-Fi field below
  return pruneUndefined({
    // make/model/os/resolution — all platforms (bridge → Tizen/webOS → UA/screen)
    ...collectCoreInfo(),
    platform,
    timezone: getTimezone(), // all platforms (Intl API)
    type, // passed in by app (backend device type)
    browser: getBrowserInfo(), // all platforms (parse UA)
    screen: getScreenInfo(), // all platforms (window.screen)
    cpuCores: getCpuCores(), // all platforms (hardwareConcurrency)
    ramGB: getRamGB(), // Chromium only (deviceMemory, coarse); Safari/Firefox → none
    battery: platform === "tv" ? undefined : await getBatteryInfo(), // Chromium desktop/Android; iOS/Safari → none
    firmware: getFirmware(), // Android bridge + Samsung + LG; web/iOS → none
    deviceId: getDeviceId(), // Android bridge + Samsung (DUID); webOS/web/iOS → none
    refreshRate:
      window.DeviceBridge?.getDeviceRefreshRate?.() || refreshRateCache, // bridge = exact Hz; else rAF estimate (all platforms)
    audioInput: audioInputCache, // all platforms (enumerateDevices, needs HTTPS)
    wifiBand: wifi.band, // Android bridge ONLY; Tizen/webOS/iOS/web → no API
    signal:
      (wifi.signal !== undefined ? String(wifi.signal) : undefined) ??
      tizenSignalCache, // 0-100 on both paths: Android bridge (RSSI→%) + Samsung (systeminfo); webOS (privileged)/iOS/web → none
    rssi: wifi.rssi, // raw dBm — same measurement as signal, Android only
    linkSpeedMbps: wifi.linkSpeedMbps, // Android only
    txLinkSpeedMbps: wifi.txLinkSpeedMbps, // API 29+; matches linkSpeedMbps on all tested hardware
    rxLinkSpeedMbps: wifi.rxLinkSpeedMbps, // API 29+; absent on chipsets that don't report it
    thermalState:
      window.DeviceBridge?.getDeviceThermalState?.() ||
      thermalStateCache ||
      undefined, // Android bridge + web Chromium (Compute Pressure); Tizen/webOS/iOS → none
    ramPressure: window.DeviceBridge?.getDeviceRamPressure?.() || undefined, // Android bridge ONLY; web/Tizen/webOS/iOS → no API
    // vpnProxy: server-side (BE infers from IP) — not client-detectable
  });
}
// -------------------------
